import "../LiensSectionPage.scss";

import { useEffect, useRef } from "react";
import type { DefaultValues } from "react-hook-form";
import { useForm } from "react-hook-form";
import { useGetLienArgumentsQuery } from "services/lien-api";
import type { ConstantTypes } from "types/DataTypes";
import { useToken } from "utils/context";
import { useSelector } from "utils/redux";

import SwimLaneHeader from "../../../components/SwimLaneHeader/SwimLaneHeader";
import { getPartTypeTitle } from "../lien-utils";
import type { ArgumentTypeChoices, Lien, LienFormValues } from "../LienTypes";
import { BackgroundJobsDrawer } from "components/BackgroundJobsDrawer/BackgroundJobsDrawer";
import { useParams } from "react-router-dom";

import LienOverviewSection from "./LienOverviewSection";
import PartTypeInformationSection from "./LienPartTypeInfoSection";
import LienSubrogationSection from "./LienSubrogationSection";
import KaboodleToaster from "components/common/KaboodleToaster";

type LienDetailPageProps = {
  lien: Lien;
  onSave: (field: string | null, value: unknown) => Promise<void>;
};

const LienDetailPage = ({ lien, onSave }: LienDetailPageProps) => {
  const { lienFileId } = useParams<{ lienFileId: string }>();
  const { accessToken } = useToken();

  const defaultValues: DefaultValues<LienFormValues> = {
    ...lien,
    orm_indicator: lien.orm_indicator ? "true" : "false",
    first_name: lien.file?.claimants?.[0]?.first_name ?? "",
    middle_name: lien.file?.claimants?.[0]?.middle_name ?? "",
    last_name: lien.file?.claimants?.[0]?.last_name ?? "",
    gender: lien.file?.claimants?.[0]?.gender ?? "",
    address: lien.file?.claimants?.[0]?.address ?? "",
    city: lien.file?.claimants?.[0]?.city ?? "",
    state: lien.file?.claimants?.[0]?.state ?? "",
    zip_code: lien.file?.claimants?.[0]?.zip_code ?? "",
    birth_date: lien.file?.claimants?.[0]?.birth_date?.toString() ?? "",
    current_conditional_payment_amount:
      lien.current_conditional_payment_amount ?? null,
    date_responded_to_client: lien.date_responded_to_client ?? "",
    date_of_closure: lien.date_of_closure ?? "",
    lien_verification_received_on: lien.lien_verification_received_on ?? "",
    is_represented: lien.file?.is_represented ?? false,
    reasonable_expectation:
      lien.file?.claimants?.[0]?.reasonable_expectation ?? false,
    defense_counsel: lien.file?.defense_counsel ?? "",
    total_amount_disputed: lien.file?.next_total_amount_disputed ?? 0,
    // add TPOC fields
    // @ts-expect-error - fix this
    tpoc_amount_1: String(lien.tpoc_amount_1 ?? ""),
    tpoc_date_1: String(lien.tpoc_date_1 ?? ""),
    // @ts-expect-error - fix this
    tpoc_amount_2: String(lien.tpoc_amount_2 ?? ""),
    tpoc_date_2: String(lien.tpoc_date_2 ?? ""),
    // @ts-expect-error - fix this
    tpoc_amount_3: String(lien.tpoc_amount_3 ?? ""),
    tpoc_date_3: String(lien.tpoc_date_3 ?? ""),
    // @ts-expect-error - fix this
    tpoc_amount_4: String(lien.tpoc_amount_4 ?? ""),
    tpoc_date_4: String(lien.tpoc_date_4 ?? ""),
    // @ts-expect-error - fix this
    tpoc_amount_5: String(lien.tpoc_amount_5 ?? ""),
    tpoc_date_5: String(lien.tpoc_date_5 ?? ""),
  };

  const { control, getValues, reset, setValue, formState } =
    useForm<LienFormValues>({
      defaultValues,
    });

  const constants = useSelector((state) => state.shared.constants);
  const prevLienIdRef = useRef(lien.id);

  const { data: lienArguments } = useGetLienArgumentsQuery(
    { lienId: lien.id.toString() },
    { skip: !lien.id },
  );

  useEffect(() => {
    // track the current lien ID so we can use keepDirtyValues to prevent form reset onChange
    // if the user is switching liens, that's when we want to reset the form with the new lien data
    const isSwitchingLiens = prevLienIdRef.current !== lien.id;
    prevLienIdRef.current = lien.id;

    const argumentsDefaultValues = lienArguments?.results?.reduce(
      (acc, arg) => {
        acc[arg.id] = {
          argument_type: arg.argument_type,
          total: arg.total,
          rationale: arg.rationale || "",
        };
        return acc;
      },
      {} as LienFormValues["arguments"],
    );

    reset(
      {
        ...lien,
        orm_indicator: lien.orm_indicator ? "true" : "false",
        first_name: lien.file?.claimants?.[0]?.first_name ?? "",
        middle_name: lien.file?.claimants?.[0]?.middle_name ?? "",
        last_name: lien.file?.claimants?.[0]?.last_name ?? "",
        gender: lien.file?.claimants?.[0]?.gender ?? "",
        address: lien.file?.claimants?.[0]?.address ?? "",
        city: lien.file?.claimants?.[0]?.city ?? "",
        state: lien.file?.claimants?.[0]?.state ?? "",
        zip_code: lien.file?.claimants?.[0]?.zip_code ?? "",
        // @ts-expect-error - fix this
        birth_date: lien.file?.claimants?.[0]?.birth_date ?? "",
        current_conditional_payment_amount:
          lien.current_conditional_payment_amount ?? null,
        date_responded_to_client: lien.date_responded_to_client ?? "",
        date_of_closure: lien.date_of_closure ?? "",
        lien_verification_received_on: lien.lien_verification_received_on ?? "",
        is_represented: lien.file?.is_represented ?? false,
        reasonable_expectation:
          lien.file?.claimants?.[0]?.reasonable_expectation ?? false,
        defense_counsel: lien.file?.defense_counsel ?? "",
        total_amount_disputed: lien.file?.next_total_amount_disputed ?? 0,
        arguments: argumentsDefaultValues,
      },
      {
        keepDirtyValues: !isSwitchingLiens,
      },
    );
  }, [lien, lienArguments, reset, formState.dirtyFields]);

  const handleFieldChange = async (field: string | null, value: unknown) => {
    try {
      await onSave(field, value);
    } catch (error) {
      console.error("Error saving field:", error);
      KaboodleToaster.error(`${error.data.detail}`);
    }
  };

  if (!lien) {
    return null;
  }

  return (
    <div className="grid-layout no-gap-tb your-tasks-3 swimlanes">
      <SwimLaneHeader label={getPartTypeTitle(lien.part_type)} />
      <SwimLaneHeader label="Overview Information" />
      <SwimLaneHeader label="Subrogation Information" />

      <PartTypeInformationSection
        lien={lien}
        control={control}
        getValues={getValues}
        setValue={setValue}
        handleFieldChange={handleFieldChange}
        lienArguments={lienArguments?.results}
        constants={
          constants as ConstantTypes & {
            ArgumentTypeChoices?: ArgumentTypeChoices[];
          }
        }
        accessToken={accessToken}
      />

      <LienOverviewSection
        control={control}
        getValues={getValues}
        setValue={setValue}
        handleFieldChange={handleFieldChange}
        constants={constants}
      />

      <LienSubrogationSection
        lien={lien}
        control={control}
        handleFieldChange={handleFieldChange}
        constants={constants}
      />
      {lienFileId && (
        <BackgroundJobsDrawer filters={{ lien_file_id: lienFileId }} />
      )}
    </div>
  );
};

export default LienDetailPage;
